import { Editor, isNodeSelection } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';
// `@tiptap/react/menus` is a v3 `exports`-only subpath the repo's classic
// `moduleResolution: node` (consumer lib type-checks) can't resolve — Vite and
// bundler tsc both can (see menus-resolution.spec.tsx). We use @ts-ignore rather
// than @ts-expect-error because the storybook tsconfig compiles this file under
// bundler, where the import resolves and there'd be no error to "expect".
// Runtime is unaffected.
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { BubbleMenu } from '@tiptap/react/menus';
import { FC, useEffect, useState } from 'react';

import { Icon, IconName, Stack, cn } from '@eluve/components';

import { SLASH_COMMAND_EXTENSION_NAME } from '../extensions/slash-command';
import { SnippetModal } from '../extensions/snippets';
import {
  getEditorExtensionOptions,
  getEditorSelectedHTML,
} from '../extensions/utils';

import { NodeSelector } from './node-selector';

export interface BubbleMenuItem {
  name: string;
  isActive: () => boolean;
  command: () => void;
  icon?: IconName;
  text?: string;
}

type EditorBubbleMenuProps = {
  editor: Editor | null;
};

export const EditorBubbleMenu: FC<EditorBubbleMenuProps> = ({ editor }) => {
  const [isNodeSelectorOpen, setIsNodeSelectorOpen] = useState(false);
  const [isSnippetModalOpen, setIsSnippetModalOpen] = useState(false);
  const [snippetContent, setSnippetContent] = useState('');
  const [snippetTitle, setSnippetTitle] = useState('');

  // v3's BubbleMenu has no `onHidden` (v2 cleared this via tippyOptions.onHidden),
  // so collapse the node selector whenever the menu's trigger goes away — i.e. the
  // selection changes or the editor blurs. Otherwise the selector stays expanded
  // into the next selection's bubble menu.
  useEffect(() => {
    if (!editor) return;
    const closeNodeSelector = () => setIsNodeSelectorOpen(false);
    editor.on('selectionUpdate', closeNodeSelector);
    editor.on('blur', closeNodeSelector);
    return () => {
      editor.off('selectionUpdate', closeNodeSelector);
      editor.off('blur', closeNodeSelector);
    };
  }, [editor]);

  // do not show BubbleMenu if editor is not editable
  if (!editor?.isEditable) {
    return null;
  }

  const stylingItems: BubbleMenuItem[] = [
    {
      name: 'bold',
      isActive: () => editor?.isActive('bold') ?? false,
      command: () => editor?.chain().focus().toggleBold().run(),
      icon: 'Bold',
    },
    {
      name: 'italic',
      isActive: () => editor?.isActive('italic') ?? false,
      command: () => editor?.chain().focus().toggleItalic().run(),
      icon: 'Italic',
    },
    {
      name: 'underline',
      isActive: () => editor?.isActive('underline') ?? false,
      command: () => editor?.chain().focus().toggleUnderline().run(),
      icon: 'Underline',
    },
    {
      name: 'strike',
      isActive: () => editor?.isActive('strike') ?? false,
      command: () => editor?.chain().focus().toggleStrike().run(),
      icon: 'Strikethrough',
    },
  ];

  // Only display snippet option if it's enabled in extension
  const slashCommandExtension = getEditorExtensionOptions(
    editor,
    SLASH_COMMAND_EXTENSION_NAME,
  );

  const snippetItems: BubbleMenuItem[] = slashCommandExtension?.snippetOptions
    ?.enabled
    ? [
        {
          name: 'add_snippet',
          isActive: () => false,
          command: () => {
            const selectedHTML = getEditorSelectedHTML(editor!);

            setIsSnippetModalOpen(true);
            setSnippetContent(selectedHTML);
          },
          text: 'Create Snippet',
        },
      ]
    : [];

  const items = [...stylingItems, ...snippetItems];

  const resetSnippetModal = () => {
    setIsSnippetModalOpen(false);
    setSnippetContent('');
    setSnippetTitle('');
  };

  return (
    <>
      <BubbleMenu
        editor={editor}
        shouldShow={({ state }: { state: EditorState }) => {
          // Hide while the snippet modal is open so it doesn't overlap.
          if (isSnippetModalOpen) {
            return false;
          }
          const { selection } = state;
          const { empty } = selection;
          // don't show bubble menu if:
          // - the selection is empty
          // - the selection is a node selection (for drag handles)
          if (empty || isNodeSelection(selection)) {
            return false;
          }
          return true;
        }}
        className="flex w-fit divide-x divide-border-primary rounded-sm border border-border-primary bg-white shadow-xl"
      >
        <NodeSelector
          editor={editor!}
          isOpen={isNodeSelectorOpen}
          setIsOpen={() => {
            setIsNodeSelectorOpen(!isNodeSelectorOpen);
          }}
        />

        <Stack direction="horizontal" align="stretch">
          {items.map((item, index) => (
            <button
              key={index}
              onClick={item.command}
              className="p-2 text-text-tertiary hover:bg-bg-tertiary active:bg-border-primary"
              type="button"
            >
              {item.icon ? (
                <Icon
                  name={item.icon}
                  className={cn({
                    'text-bg-accent-strong-primary': item.isActive(),
                  })}
                />
              ) : (
                <span className="text-sm font-medium text-text-tertiary">
                  {item.text}
                </span>
              )}
            </button>
          ))}
        </Stack>
      </BubbleMenu>
      <SnippetModal
        snippet={{ title: snippetTitle, content: snippetContent }}
        isOpen={isSnippetModalOpen}
        onOpenChange={setIsSnippetModalOpen}
        onClose={resetSnippetModal}
        onSave={async (title, content) => {
          await slashCommandExtension?.snippetOptions?.addSnippet?.(
            title,
            content,
          );
          resetSnippetModal();
        }}
      />
    </>
  );
};
